import { useMutation, useQueryClient } from '@tanstack/react-query'
import { waitForUploadJob } from '@/api/docsAPI'
import { serverURL } from '../api/config'

type UploadResult = Array<{ id: string }>
type UploadJobResult = {
  id?: number | string
  doc_responses?: Array<{ id: number | string }>
}

function mapCompletedUploadResults(results: UploadJobResult[]): UploadResult {
  return results.flatMap((result) => {
    if (result.id !== undefined) return [{ id: String(result.id) }]
    return (
      result.doc_responses?.map((doc) => ({
        id: String(doc.id),
      })) ?? []
    )
  })
}

/**
 * Upload org docs and return parsed response (Array<{id}>).
 * Unlike the shared useUploadOrgDocMutation which returns boolean,
 * this returns the doc IDs that UploadComponent needs.
 */
export function useUploadOrgDocs(orgUuid: string | null, token: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (files: File[]): Promise<UploadResult> => {
      const formData = new FormData()
      files.forEach((file) => formData.append('files', file))

      const response = await fetch(`${serverURL}/orgs/${orgUuid}/docs`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      if (!response.ok) throw new Error(`Upload failed: ${response.status}`)

      const job: { job_id: string } = await response.json()
      const results = (await waitForUploadJob({
        token,
        jobId: job.job_id,
      })) as UploadJobResult[]
      return mapCompletedUploadResults(results)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgDocs', orgUuid] })
    },
  })
}

/**
 * Upload funding need docs and return parsed response (Array<{id}>).
 */
export function useUploadFundingNeedDocs(
  orgUuid: string | null,
  fundingNeedId: number | null,
  token: string,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (files: File[]): Promise<UploadResult> => {
      const formData = new FormData()
      files.forEach((file) => formData.append('files', file))

      const response = await fetch(
        `${serverURL}/funding-needs/${orgUuid}/${fundingNeedId}/docs`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      )
      if (!response.ok) throw new Error(`Upload failed: ${response.status}`)

      const job: { job_id: string } = await response.json()
      const results = (await waitForUploadJob({
        token,
        jobId: job.job_id,
      })) as UploadJobResult[]
      return mapCompletedUploadResults(results)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['fundingNeedDocs', orgUuid, fundingNeedId],
      })
    },
  })
}
